import React, {useEffect, useState} from 'react';
import Container from 'react-bootstrap/Container';
import {Route, Routes, Navigate} from "react-router";
import Footer from './components/page/Footer';
import { createBrowserHistory } from 'history';
import Locations from './components/locations/Locations';
import LeaderboardVisits from './components/leaderboard/LeaderboardVisits';
import Navigation from "./components/nav/Navigation";
import Home from './components/home/Home';
import Header from './components/page/Header';
// @ts-ignore
import AuthEmail from './components/auth/AuthEmail';
import MapElement from "./components/map/MapElement";
import Journey from "./components/map/Journey";
import Admin from "./components/admin/Admin";
import AdminHome from "./components/admin/AdminHome";
import AdminPage from "./components/admin/AdminPage";
import Support from "./components/support/Support";
import WhatsNew from "./components/whats-new/WhatsNew";
import AuthEmailResponse from "./components/auth/AuthEmailResponse";
import {getAuth, onAuthStateChanged, User as FirebaseAuthUser} from "@firebase/auth";
import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  serverTimestamp,
  doc,
  updateDoc,
  onSnapshot
} from "@firebase/firestore";
import {db} from "./firebase/FirebaseProvider";
import {Location, Notifications, Session, SettingsDoc, Stats, User, UserBadge} from "./components/types/Types";
import Visits from "./components/hub/Visits";
import Comments from "./components/hub/Comments";
import Updates from "./components/hub/Updates";
import MyProfile from "./components/users/MyProfile";
import LocationComments from "./components/locations/profile/LocationComments";
import LocationVisits from "./components/locations/profile/LocationVisits";
import LocationStays from "./components/locations/profile/LocationStays";
import { logDataLoaded } from "./components/util/Util";
// Google Analytics
import ReactGA from 'react-ga';
import {LOCATION_LIMIT, USERS_LIMIT} from "./components/util/Util";
import MyLocation from "./components/locations/profile/MyLocation";
import Sessions from "./components/hub/Sessions";
import UserVisits from "./components/users/tabs/UserVisits";
import UserStays from "./components/users/tabs/UserStays";
import UserOverview from "./components/users/UserOverview";
import UserSessions from "./components/users/tabs/UserSessions";
import LocationSessions from "./components/locations/profile/LocationSessions";
import LeaderboardVerified from "./components/leaderboard/LeaderboardVerified";
import LeaderboardSessions from "./components/leaderboard/LeaderboardSessions";
import LeaderboardPhotos from "./components/leaderboard/LeaderboardPhotos";
import LeaderboardStays from "./components/leaderboard/LeaderboardStays";
import Chat from "./components/chat/Chat";
import Settings from "./components/settings/Settings";
import LeaderboardYear from "./components/leaderboard/LeaderboardYear";
import LocationPhotos from "./components/locations/profile/LocationPhotos";
import Photos from "./components/hub/Photos";
import PhotosViewer from "./components/photos/PhotosViewer";
import UserComments from "./components/users/tabs/UserComments";
import UserPhotos from "./components/users/tabs/UserPhotos";
import PhotoEditor from "./components/photos/PhotoEditor";
import UserPoints from "./components/users/tabs/UserPoints";
import UserLikes from "./components/users/tabs/UserLikes";
import CustomRouter from "./CustomRouter";
import Events from "./components/events/Events";

const TRACKING_ID = "G-27JNWX5874";
ReactGA.initialize(TRACKING_ID);

async function recordLastSeenAt(userId: string): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId);
    const data = {
      lastSeenAt: serverTimestamp(),
      id: userId,
    };
    await updateDoc(docRef, data);
  } catch (error) {
    console.error("Error: ", error);
  }
}

async function recordUserLogin(userId: string, userName: string): Promise<void> {
  try {
    await addDoc(collection(db, 'user-logins'), {
      userId,
      userName,
      loggedInAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error recording user login: ", error);
  }
}

const App: React.FC = () => {
  const history = createBrowserHistory();

  const [currentUser, setCurrentUser] = useState<User | undefined>(undefined);
  const [authResolved, setAuthResolved] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userSession, setUserSession] = useState<Session | undefined>(undefined);
  const [users, setUsers] = useState<Array<User>>([]);
  const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map<string, User>());
  const [locations, setLocations] = useState<Array<Location>>([]);
  const [sessions, setSessions] = useState<Array<Session>>([]);
  const [sessionsMap, setSessionsMap] = useState<Map<string, Session>>(new Map<string, Session>());
  const [likesMap, setLikesMap] = useState<Map<string, boolean>>(new Map<string, boolean>());
  const [stats, setStats] = useState<Stats | undefined>(undefined);
  const [notifications, setNotifications] = useState<Notifications | undefined>(undefined);
  const [settings, setSettings] = useState<SettingsDoc>({chatEnabled: false} as SettingsDoc);
  const [photos, setPhotos] = useState<Array<any>>([]);

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  useEffect(() => {
    const auth = getAuth();
    let unsubscribeUserSnapshot: (() => void) | undefined;
    let recordedLoginAuthId: string | undefined;

    const loadAuthenticatedUser = async (authUser: FirebaseAuthUser, recordLogin: boolean) => {
      let userCollectionSnap = await getDocs(query(collection(db, 'users'), where('authId', '==', authUser.uid)));

      // Google sign-in can complete before the authId mapping has been written.
      // Fall back to the verified email so the app can update in place.
      if (userCollectionSnap.empty && authUser.email) {
        userCollectionSnap = await getDocs(query(collection(db, 'users'), where('email', '==', authUser.email.trim().toLowerCase())));
      }

      userCollectionSnap.forEach((userDoc) => {
        const userId = userDoc.id;
        const userData = userDoc.data() as User;
        if (recordLogin && recordedLoginAuthId !== authUser.uid) {
          recordedLoginAuthId = authUser.uid;
          recordLastSeenAt(userId);
          recordUserLogin(userId, userData.name || authUser.displayName || "");
        }

        unsubscribeUserSnapshot?.();
        unsubscribeUserSnapshot = onSnapshot(doc(db, 'users', userId), (doc) => {
          if (doc.exists()) {
            let userToSet = doc.data() as User;
            userToSet.id = doc.id;
            setCurrentUser((prev) => ({ ...userToSet, badges: prev?.badges }));
          }
        });
      });
    };

    const handleUserProfileReady = () => {
      if (auth.currentUser) {
        void loadAuthenticatedUser(auth.currentUser, true);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      setAuthResolved(true);
      setIsAuthenticated(!!authUser);

      if (!authUser) {
        unsubscribeUserSnapshot?.();
        unsubscribeUserSnapshot = undefined;
        setCurrentUser(undefined);
        return;
      }

      await loadAuthenticatedUser(authUser, true);
    });

    window.addEventListener('stl-user-profile-ready', handleUserProfileReady);

    return () => {
      unsubscribe();
      unsubscribeUserSnapshot?.();
      window.removeEventListener('stl-user-profile-ready', handleUserProfileReady);
    };
  }, []);

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    const badgesQuery = query(
      collection(db, 'user-badges'),
      where('userId', '==', currentUser.id)
    );
    const unsubscribe = onSnapshot(badgesQuery, (snapshot) => {
      const badges: Array<UserBadge> = [];
      snapshot.forEach((doc) => {
        badges.push({ ...doc.data(), id: doc.id } as UserBadge);
      });
      badges.sort((a, b) => a.order - b.order);
      setCurrentUser((prev) => prev ? { ...prev, badges } : prev);
      setUsers((prev) => prev.map(u => u.id === currentUser.id ? { ...u, badges } : u));
      setUsersMap((prev) => {
        const updated = new Map(prev);
        const existing = updated.get(currentUser.id);
        if (existing) updated.set(currentUser.id, { ...existing, badges });
        return updated;
      });
    });
    return () => unsubscribe();
  }, [currentUser?.id]);

  useEffect(() => {
    (async () => {
      // Calculate date 10 years ago
      const tenYearsAgo = new Date();
      tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
      
      const usersCollection = query(collection(db, 'users'),
          where('status', '==', 'ACTIVE'),
          orderBy('visitCount', 'desc'),
          limit(USERS_LIMIT)
      );
      const usersCollectionSnapshot = await getDocs(usersCollection);
      let users: Map<string, User> = new Map<string, User>();
      let usersArray: Array<User> = new Array<User>();
      usersCollectionSnapshot.forEach((doc) => {
        let userToSet = doc.data() as User;
        userToSet.id = doc.id;
        users.set(userToSet.id, userToSet);
        usersArray.push(userToSet);
      });
      
      // Load all active badges
      const badgesCollection = query(
        collection(db, 'user-badges'),
        where('status', '==', 'ACTIVE')
      );
      const badgesSnapshot = await getDocs(badgesCollection);
      const badgesByUserId = new Map<string, Array<UserBadge>>();
      
      badgesSnapshot.forEach((doc) => {
        const badge = { ...doc.data(), id: doc.id } as UserBadge;
        const userId = badge.userId;
        if (!badgesByUserId.has(userId)) {
          badgesByUserId.set(userId, []);
        }
        badgesByUserId.get(userId)?.push(badge);
      });
      
      // Attach sorted badges to each user
      usersArray.forEach(user => {
        const userBadges = badgesByUserId.get(user.id) || [];
        user.badges = userBadges.sort((a, b) => a.order - b.order);
      });
      
      logDataLoaded('users', usersArray.length);
      setUsers(usersArray);
      setUsersMap(users);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      let locationsArray: Location[] = [];
      const locationsOnceCollection = query(collection(db, 'locations'),
          orderBy('name', 'asc'),
          limit(LOCATION_LIMIT));
      const locationsCollectionSnapshot = await getDocs(locationsOnceCollection);
      locationsCollectionSnapshot.forEach((doc) => {
        let tempLocation = doc.data() as Location;
        tempLocation.id = doc.id;
        if (!tempLocation.name) {
          console.log('Location does not have a name: ' + doc.id);
          tempLocation.name = 'Wetherspoons';
        }
        if (tempLocation.enabled === undefined || tempLocation.enabled) {
          locationsArray.push(tempLocation);
        }
      });
      logDataLoaded('locations', locationsArray.length);
      setLocations(locationsArray);
    })();
  }, []);

  const processSessions = (snapshot: any, userId: string) => {
    let sessions = new Array<Session>();
    let sessionsMap: Map<string, Session> = new Map<string, Session>();
    const now = new Date();
    
    snapshot.forEach((doc: any) => {
      let tempSession = doc.data() as Session;
      tempSession.id = doc.id;
      
      // Convert Firestore Timestamp to Date if needed
      const checkOutTime = tempSession.checkOutTime instanceof Date 
        ? tempSession.checkOutTime 
        : (tempSession.checkOutTime as any).toDate();
      
      // Only include sessions that haven't checked out yet
      if (checkOutTime > now) {
        sessions.push(tempSession);
        sessionsMap.set(tempSession.userId, tempSession);
        if (tempSession.userId === userId) {
          setUserSession(tempSession);
        }
      }
    });
    logDataLoaded('sessions', sessions.length);
    
    setSessionsMap(sessionsMap);
    setSessions(sessions);
  };

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    
    const sessionsQuery = query(collection(db, 'sessions'),
        where('checkOutTime', '>', new Date()),
        orderBy('checkOutTime', 'desc'),
        limit(LOCATION_LIMIT));
    
    let latestSnapshot: any = null;
    
    const unsubscribe = onSnapshot(sessionsQuery, (snapshot) => {
      latestSnapshot = snapshot;
      processSessions(snapshot, currentUser.id);
    });
    
    // Periodically re-filter sessions to remove expired ones
    // This ensures the UI updates even when Firestore doesn't send updates
    const intervalId = setInterval(() => {
      if (latestSnapshot) {
        processSessions(latestSnapshot, currentUser.id);
      }
    }, 60000); // Check every minute
    
    return () => {
      unsubscribe();
      clearInterval(intervalId);
    };
  }, [currentUser]);

  const refreshSessions = async () => {
    if (!currentUser) {
      return;
    }
    
    // Force a fresh fetch from Firebase
    const sessionsQuery = query(collection(db, 'sessions'),
        where('checkOutTime', '>', new Date()),
        orderBy('checkOutTime', 'desc'),
        limit(LOCATION_LIMIT));
    
    const snapshot = await getDocs(sessionsQuery);
    processSessions(snapshot, currentUser.id);
  };

  // Listen for likes from the current user
  useEffect(() => {
    if (!currentUser) {
      return;
    }
    
    const likesQuery = query(collection(db, 'likes'),
        where('userId', '==', currentUser.id));
    
    const unsubscribe = onSnapshot(likesQuery, (snapshot) => {
      const newLikesMap = new Map<string, boolean>();
      snapshot.forEach((doc) => {
        const likeData = doc.data();
        newLikesMap.set(likeData.entityId, true);
      });
      setLikesMap(newLikesMap);
    });
    
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    (async () => {
      if (!currentUser) {
        return;
      }
      const statsOnceCollection = query(collection(db, 'stats'));
      const statsCollectionSnapshot = await getDocs(statsOnceCollection);
      statsCollectionSnapshot.forEach((doc) => {
        if (doc.id === "counts") {
          const stats = doc.data() as Stats;
          setStats(stats)
        }
      });
      logDataLoaded('stats', statsCollectionSnapshot.size);
    })();
  }, [currentUser]);

  useEffect(() => {
    (async () => {
      if (!currentUser) {
        return;
      }
      onSnapshot(query(collection(db, 'notifications')), (snapshot) => {
        snapshot.docChanges().forEach(notificationTimings => {
          if (notificationTimings.doc.id === 'messages') {
            setNotifications(notificationTimings.doc.data() as Notifications)
          }
        });
      });
    })();
  }, [currentUser]);

  useEffect(() => {
    (async () => {
      if (!currentUser) {
        return;
      }
      onSnapshot(query(collection(db, 'settings')), (snapshot) => {
        snapshot.docChanges().forEach(settings => {
          setSettings(settings.doc.data() as SettingsDoc)
        });
      });
    })();
  }, [currentUser]);

  return (
      <CustomRouter history={history}>
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Header sessions={sessions}
                  session={userSession}
                  notifications={notifications}
                  currentUser={currentUser}
                  setCurrentUser={setCurrentUser}
                  settings={settings}
                  refreshSessions={refreshSessions}/>
          <Navigation currentUser={currentUser} settings={settings}/>

          <Container style={{ flex: 1 }}>
          <Routes>

            {/*No auth required*/}
            <Route path="/" element={!authResolved ? <div className={'loading'}>Loading...</div> : isAuthenticated ? <Navigate to="/home" replace /> : <AuthEmail />} />
            <Route path="/auth/response" element={<AuthEmailResponse />} />
            <Route path="/home" element={<Home settings={settings} currentUser={currentUser} stats={stats} locations={locations} />} />
            <Route path="/login" element={!authResolved ? <div className={'loading'}>Loading...</div> : isAuthenticated ? <Navigate to="/home" replace /> : <AuthEmail />} />
            <Route path="/support" element={<Support settings={settings} currentUser={currentUser} />} />
            <Route path="/updates" element={<WhatsNew settings={settings} currentUser={currentUser} />} />

            {/*Auth Required*/}
            <Route path="/pubs" element={<Locations locations={locations} />} />
            <Route path="/map" element={<MapElement sessions={sessions} locations={locations} currentUser={currentUser} />} />
            <Route path="/journey" element={<Journey locations={locations} currentUser={currentUser} />} />
            <Route path="/journey/:id" element={<Journey locations={locations} currentUser={currentUser} />} />
            <Route path="/settings" element={<Settings currentUser={currentUser} locations={locations} settings={settings} />} />
            <Route path="/admin/:id" element={<Admin />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin-page/:tab" element={<AdminPage currentUser={currentUser} usersMap={usersMap} sessionsMap={sessionsMap} locations={locations} />} />
            <Route path="/admin-page" element={<AdminPage currentUser={currentUser} usersMap={usersMap} sessionsMap={sessionsMap} locations={locations} />} />
            <Route path="/admin-home/:tab" element={<AdminHome currentUser={currentUser} />} />
            <Route path="/admin-home" element={<AdminHome currentUser={currentUser} />} />

            {/*Leaderboard specific*/}
            <Route path="/leaderboard/visits" 
                   element={<LeaderboardVisits users={users} currentUser={currentUser} sessionsMap={sessionsMap} />} />
            <Route path="/leaderboard/verified" 
                   element={<LeaderboardVerified users={users} currentUser={currentUser} sessionsMap={sessionsMap} />} />
            <Route path="/leaderboard/sessions" 
                   element={<LeaderboardSessions users={users} currentUser={currentUser} sessionsMap={sessionsMap} />} />
            <Route path="/leaderboard/photos" 
                   element={<LeaderboardPhotos users={users} currentUser={currentUser} sessionsMap={sessionsMap} />} />
            <Route path="/leaderboard/stays"
                   element={<LeaderboardStays users={users} currentUser={currentUser} sessionsMap={sessionsMap} />} />
            <Route path="/leaderboard/year" 
                   element={<LeaderboardYear userMap={usersMap} users={users} currentUser={currentUser} sessionsMap={sessionsMap} locations={locations} />} />

            {/*User specific*/}
            <Route path="/profile/:id"
                   element={<UserOverview currentUser={currentUser} locations={locations} />} />
            <Route path="/profile/:id/visits" 
                   element={<UserVisits currentUser={currentUser} locations={locations} />} />
            <Route path="/profile/:id/stays"
                   element={<UserStays currentUser={currentUser} locations={locations} />} />
            <Route path="/profile/:id/sessions" 
                   element={<UserSessions currentUser={currentUser} locations={locations} />} />
            <Route path="/profile/:id/comments" 
                   element={<UserComments currentUser={currentUser} likesMap={likesMap} locations={locations} />} />
            <Route path="/profile/:id/photos" 
                   element={<UserPhotos currentUser={currentUser} likesMap={likesMap} locations={locations} />} />
            <Route path="/profile/:id/likes" 
                   element={<UserLikes currentUser={currentUser} locations={locations} />} />
            <Route path="/profile/:id/points" 
                   element={<UserPoints currentUser={currentUser} locations={locations} />} />
            <Route path="/profile/:id/visits/me" 
                   element={<MyProfile currentUser={currentUser} locations={locations} />} />

            {/*Location specific*/}
            <Route path="/location/:id/comments" 
                   element={<LocationComments currentUser={currentUser} settings={settings} usersMap={usersMap} likesMap={likesMap} />} />
            <Route path="/location/:id/photos" 
                   element={<LocationPhotos currentUser={currentUser} settings={settings} usersMap={usersMap} likesMap={likesMap} />} />
            <Route path="/location/:id/comments/me" 
                   element={<MyLocation currentUser={currentUser} settings={settings} usersMap={usersMap} likesMap={likesMap} />} />
            <Route path="/location/:id/visits" 
              element={<LocationVisits currentUser={currentUser} settings={settings} usersMap={usersMap} sessionsMap={sessionsMap} likesMap={likesMap} />} />
            <Route path="/location/:id/stays"
              element={<LocationStays currentUser={currentUser} settings={settings} usersMap={usersMap} sessionsMap={sessionsMap} likesMap={likesMap} />} />
            <Route path="/location/:id/sessions" 
                   element={<LocationSessions currentUser={currentUser} settings={settings} usersMap={usersMap} likesMap={likesMap}  />} />

            {/*Hub specific*/}
            <Route path="/hub/visits"
                   element={currentUser?.socialDisabled ? <Navigate to="/home" replace /> : <Visits currentUser={currentUser} usersMap={usersMap} sessionsMap={sessionsMap} />} />
            <Route path="/hub/comments"
                   element={currentUser?.socialDisabled ? <Navigate to="/home" replace /> : <Comments currentUser={currentUser} usersMap={usersMap} sessionsMap={sessionsMap} likesMap={likesMap} />} />
            <Route path="/hub/sessions"
                   element={currentUser?.socialDisabled ? <Navigate to="/home" replace /> : <Sessions currentUser={currentUser} usersMap={usersMap} />} />
            <Route path="/hub/photos"
                   element={currentUser?.socialDisabled ? <Navigate to="/home" replace /> : <Photos currentUser={currentUser} usersMap={usersMap} sessionsMap={sessionsMap} likesMap={likesMap} photos={photos} setPhotos={setPhotos} />} />

            {/*Photo specific*/}
            <Route path="/photos/:tag"
                   element={<PhotosViewer currentUser={currentUser} usersMap={usersMap} likesMap={likesMap} />} />
            <Route path="/photo/:id"
                   element={<PhotoEditor currentUser={currentUser} usersMap={usersMap} likesMap={likesMap} locations={locations} />} />

            {/*Events specific*/}
            <Route path="/events"
                   element={<Events />} />

            {/*Chat specific*/}
            <Route path="/chat/general"
                   element={<Chat notifications={notifications} selectedChannel="GENERAL"
                          usersMap={usersMap}
                          currentUser={currentUser} setNotifications={setNotifications}
                          settings={settings} />} />
            <Route path="/chat/support"
                   element={<Chat notifications={notifications} selectedChannel="SUPPORT"
                          usersMap={usersMap}
                          currentUser={currentUser} setNotifications={setNotifications}
                          settings={settings} />} />
          </Routes>
        </Container>
        {!window.location.pathname.startsWith('/journey') && <Footer />}
        </div>
      </CustomRouter>
  );
};

export default App;
